import { useCallback, useEffect, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../components/providers/AuthProvider";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { useRevenueCat } from "../hooks/useRevenueCat";
import {
  captureAnalyticsEvent,
  captureEventAndRedirect,
} from "../utils/analytics";
import { PH_EVENTS_WEB } from "../utils/analyticsEvents";
import { api } from "../utils/api";
import {
  getOfferings,
  getOfferingsAnonymous,
  initializeRevenueCat,
  initializeRevenueCatAnonymous,
  isRevenueCatInitialized,
  purchasePackage,
} from "../utils/revenuecat";
import {
  createCartAndGetCheckoutUrl,
  getProductByHandle,
} from "../utils/shopify";

export default function CheckoutSubscription() {
  const { packageId } = useParams<{ packageId: string }>();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [purchaseStarted, setPurchaseStarted] = useState(false);
  const { handleError } = useErrorHandler({
    fallbackMessage: "Failed to load user information for checkout.",
  });

  // Fetch user email for prefilling Shopify checkout
  const {
    data: userEmail,
    isLoading: emailLoading,
    error: emailError,
  } = api.user.queryUserEmail.useQuery(undefined, {
    enabled: !!user && !loading,
  });

  // Handle email query errors using useEffect
  useEffect(() => {
    if (emailError) {
      handleError(emailError);
    }
  }, [emailError, handleError]);

  // Use RevenueCat hook to handle initialization
  const {
    isInitialized,
    isLoading: rcLoading,
    error: rcError,
  } = useRevenueCat(user);

  // Define purchase function with useCallback to ensure it's stable
  const handleDirectPurchase = useCallback(async () => {
    console.log("handleDirectPurchase called, setting purchasing to true");

    if (!packageId) {
      console.log("No packageId, setting error");
      setError("No subscription package specified");
      return;
    }

    // Check direct RC status instead of hook status
    const directRCReady = isRevenueCatInitialized();
    if (!directRCReady) {
      console.log("RevenueCat not ready, setting error");
      setError("RevenueCat not yet initialized. Please wait...");
      return;
    }

    console.log("Setting purchasing state to true");
    setPurchasing(true);
    setError(null);

    try {
      console.log("Loading offerings...");
      // Load offerings to find the specific package - use anonymous version if not authenticated
      const offerings = user
        ? await getOfferings()
        : await getOfferingsAnonymous();

      if (!offerings || !offerings.current) {
        console.log("No offerings available");
        setError("No subscription offerings available");
        return;
      }

      console.log("Searching for package:", decodeURIComponent(packageId));
      // Find the specific package by identifier
      const targetPackage = offerings.current.availablePackages.find(
        (pkg: any) => pkg.identifier === decodeURIComponent(packageId),
      );

      if (!targetPackage) {
        console.log("Package not found");
        setError(
          `Subscription package "${decodeURIComponent(packageId)}" not found`,
        );
        return;
      }

      console.log("Starting purchase for package:", targetPackage.identifier);
      console.log(
        "🔍 CheckoutSubscription: User email for prefilling:",
        userEmail,
      );
      console.log("🔍 CheckoutSubscription: User authenticated:", !!user);

      // Purchase the specific package with custom parameters
      const purchaseResult = await purchasePackage({
        rcPackage: targetPackage,
        customerEmail: userEmail ?? undefined, // undefined for unauthenticated users
        skipSuccessPage: false, // don't skip revenuecat purchase success page
      });

      if (purchaseResult?.customerInfo) {
        // Check if the user now has the "Samphire membership" entitlement
        const hasEntitlement = Object.keys(
          purchaseResult.customerInfo.entitlements.active,
        ).includes("Samphire membership");

        console.log(
          "Purchase completed. Has Samphire membership entitlement:",
          hasEntitlement,
        );

        // Check if this was a Nettle package and create Shopify cart
        const isNettlePackage = packageId?.toLowerCase().includes("nettle");
        if (isNettlePackage && hasEntitlement) {
          await handleNettleCartCreation();
        } else {
          // Redirect to home for non-Nettle packages
          navigate("/");
        }
      } else {
        console.log("Purchase result missing customerInfo");
        setError(
          "Purchase completed but unable to verify subscription status.",
        );
      }
    } catch (err: any) {
      console.error("Purchase failed:", err);

      // Handle specific RevenueCat errors
      if (err && typeof err === "object" && "errorCode" in err) {
        if (err.errorCode === "UserCancelledError") {
          // User cancelled, redirect back to products
          console.log("User cancelled purchase");
          navigate("/products");
          return;
        }

        // Handle specific RevenueCat initialization error (7638)
        if (err.errorCode === 7638) {
          console.log("RevenueCat initialization error 7638");
          setError(
            "RevenueCat not fully initialized. Please try again in a moment.",
          );
          return;
        }
      }

      setError(err.message || "Purchase failed. Please try again.");
      // Reset purchase started flag on error so user can retry
      setPurchaseStarted(false);
    } finally {
      console.log(
        "handleDirectPurchase finally block, setting purchasing to false",
      );

      setPurchasing(false);
    }
  }, [packageId, navigate, userEmail, user]);

  // Handle Nettle cart creation after successful subscription
  const handleNettleCartCreation = async () => {
    try {
      console.log("Creating Shopify cart for Nettle device...");

      // Fetch the Nettle product by handle to get its variant ID
      const nettleProduct = await getProductByHandle(
        "nettle-subscription-device",
      );

      if (!nettleProduct) {
        console.error("Nettle product not found in Shopify");
        alert("Nettle product not found. Please contact support.");
        navigate("/");
        return;
      }

      // Get the first available variant
      const availableVariant = nettleProduct.variants.find(
        (v) => v.availableForSale,
      );

      if (!availableVariant) {
        console.error("No available variant found for Nettle product");
        alert("Nettle product not available. Please contact support.");
        navigate("/");
        return;
      }

      console.log("Found Nettle variant:", availableVariant.id);

      // Create a Shopify cart with the Nettle device
      const checkoutUrl = await createCartAndGetCheckoutUrl(
        availableVariant.id,
        1, // quantity
        user ?? undefined, // undefined for unauthenticated users
        userEmail ?? undefined, // Pass user email for prefilling (convert null to undefined)
      );

      if (checkoutUrl) {
        console.log(
          "Shopify cart created successfully, redirecting to checkout",
        );

        // Capture device checkout initiated and redirect
        await captureEventAndRedirect(
          posthog,
          PH_EVENTS_WEB.CHECKOUT_INITIATED,
          checkoutUrl,
          {
            channel: "shopify",
            product_handle: "nettle-subscription-device",
            package_id: packageId,
          },
        );
      } else {
        console.error("Failed to create Shopify checkout");
        alert("Failed to create checkout. Please contact support.");
        navigate("/");
      }
    } catch (error) {
      console.error("Error creating Nettle cart:", error);
      alert("Error creating Nettle checkout. Please contact support.");
      navigate("/");
    }
  };

  useEffect(() => {
    // Only run once when conditions are met
    if (purchaseStarted) return;

    // Check direct RevenueCat initialization status
    const directRCInitialized = isRevenueCatInitialized();

    // Debug logging
    console.log("CheckoutSubscription useEffect:", {
      user: !!user,
      loading,
      packageId,
      isInitialized,
      directRCInitialized,
      rcLoading,
      purchasing,
      rcError,
      purchaseStarted,
    });

    // If we have a packageId, RevenueCat is initialized, start purchase process
    // For authenticated users, also wait for userEmail to be fetched

    const baseReady =
      packageId && directRCInitialized && !purchasing && !rcLoading && !rcError;

    const shouldStartPurchase =
      baseReady &&
      (!user || (user && !emailLoading && userEmail !== undefined));

    if (shouldStartPurchase) {
      console.log("🔍 CheckoutSubscription: Starting purchase process");

      // Mark that we've started the purchase to prevent multiple attempts
      setPurchaseStarted(true);

      // Capture checkout initiation event
      captureAnalyticsEvent(posthog, PH_EVENTS_WEB.CHECKOUT_INITIATED, {
        channel: "revenuecat",
        package_id: packageId,
      });

      // Start purchase immediately (no timer needed)
      handleDirectPurchase();
    }
  }, [
    user,
    loading,
    packageId,
    rcLoading,
    purchasing,
    rcError,
    userEmail,
    emailLoading,
    purchaseStarted,
    posthog,
    handleDirectPurchase,
  ]);

  // Show loading while checking auth, initializing RevenueCat, or purchasing
  if (loading || rcLoading || purchasing) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="checkout-loading">
            <div className="loading-spinner"></div>
            <p>
              {purchasing
                ? "Processing your subscription..."
                : rcLoading
                  ? "Initializing subscription system..."
                  : "Loading checkout..."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Allow both authenticated and unauthenticated checkout

  // If there's an error from RevenueCat or our purchase flow, show it with option to go back
  if (error || rcError) {
    const displayError = error || rcError;
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="checkout-error">
            <h2>Subscription Error</h2>
            <p>{displayError}</p>
            <button
              onClick={() => navigate("/products")}
              className="btn-primary"
            >
              Back to Products
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Debug state - this fallback indicates an issue with the purchase flow
  const finalDirectRCInitialized = isRevenueCatInitialized();
  const finalRcReady = isInitialized || finalDirectRCInitialized;

  console.log("Fallback state reached - debug info:", {
    user: !!user,
    packageId,
    isInitialized,
    directRCInitialized: finalDirectRCInitialized,
    rcReady: finalRcReady,
    purchasing,
    rcLoading,
    rcError,
  });

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <div className="checkout-loading">
          <div className="loading-spinner"></div>
          <p>Preparing your subscription...</p>
          {process.env.NODE_ENV === "development" && (
            <div
              style={{
                marginTop: "1rem",
                fontSize: "0.75rem",
                color: "#6b7280",
              }}
            >
              <p>
                Debug: User: {user ? "✓ (Auth)" : "✗ (Anon)"}, Package:{" "}
                {packageId || "missing"}, Hook Init: {isInitialized ? "✓" : "✗"}
                , Direct Init: {finalDirectRCInitialized ? "✓" : "✗"}, Ready:{" "}
                {finalRcReady ? "✓" : "✗"}, RC Loading: {rcLoading ? "✓" : "✗"},
                Purchase Started: {purchaseStarted ? "✓" : "✗"}
              </p>
              <div
                style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}
              >
                <button
                  onClick={async () => {
                    console.log("Manual initialization trigger");
                    try {
                      if (user) {
                        await initializeRevenueCat(user);
                      } else {
                        await initializeRevenueCatAnonymous();
                      }
                      console.log(
                        "Manual initialization completed, new status:",
                        isRevenueCatInitialized(),
                      );
                    } catch (error) {
                      console.error("Manual initialization failed:", error);
                    }
                  }}
                  className="btn-primary"
                  style={{
                    fontSize: "0.75rem",
                    padding: "0.5rem",
                  }}
                >
                  Force Init (Dev)
                </button>
                <button
                  onClick={() => {
                    console.log("Manual purchase trigger");
                    setPurchaseStarted(false);
                    handleDirectPurchase();
                  }}
                  className="btn-primary"
                  style={{
                    fontSize: "0.75rem",
                    padding: "0.5rem",
                  }}
                >
                  Force Purchase (Dev)
                </button>
                <button
                  onClick={() => {
                    console.log("Force refresh trigger");
                    window.location.reload();
                  }}
                  className="btn-primary"
                  style={{
                    fontSize: "0.75rem",
                    padding: "0.5rem",
                  }}
                >
                  Refresh (Dev)
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
